// ClarityStack Patient Detail v2.1 — Custom fee display + Referral source
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import MainLayout from '@/components/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  Calendar, 
  Edit, 
  Save, 
  Trash2,
  ChevronDown,
  ChevronRight,
  Briefcase,
  FlaskConical,
  FileText,
  Upload,
  Download,
  Plus,
  Sparkles,
  Loader2,
  X,
  Clock,
  Video,
  MapPin,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Section Header Component - extracted to avoid nested component definition
const SectionHeader = ({ title, icon: Icon, isOpen, count }) => (
  <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50 transition-colors">
    <div className="flex items-center gap-3">
      <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
      <span className="font-medium text-foreground">{title}</span>
      {count !== undefined && (
        <Badge variant="secondary" className="text-xs">{count}</Badge>
      )}
    </div>
    {isOpen ? (
      <ChevronDown className="h-4 w-4 text-muted-foreground" />
    ) : (
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    )}
  </CollapsibleTrigger>
);

const PatientDetailPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [patient, setPatient] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Collapsible sections state
  const [openSections, setOpenSections] = useState({
    overview: true,
    clinical: true,
    history: false,
    progress: false,
    documents: false,
    sessions: false
  });
  
  // Progress notes
  const [newProgressNote, setNewProgressNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [suggestingNote, setSuggestingNote] = useState(false);
  
  // Documents
  const fileInputRef = useRef(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  // Patient Summary
  const [aiSummary, setAiSummary] = useState('');
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [savingSummary, setSavingSummary] = useState(false);
  const [summaryEdited, setSummaryEdited] = useState(false);

  // Billing settings (for default fee display)
  const [defaultFee, setDefaultFee] = useState(null);

  useEffect(() => {
    fetchPatientData();
    fetchBillingSettings();
  }, [patientId]);

  const fetchBillingSettings = async () => {
    try {
      const res = await fetch(`${API}/billing-settings`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data) setDefaultFee(data.default_session_fee);
      }
    } catch (e) {
      // silently fail — default fee is optional display
    }
  };

  const fetchPatientData = async () => {
    try {
      const [patientRes, sessionsRes] = await Promise.all([
        fetch(`${API}/patients/${patientId}`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        }),
        fetch(`${API}/sessions?patient_id=${patientId}`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        })
      ]);

      if (patientRes.ok) {
        const patientData = await patientRes.json();
        setPatient(patientData);
        setFormData(patientData);
        setAiSummary(patientData.ai_summary || '');
      } else {
        toast.error('Patient not found');
        navigate('/patients');
      }

      if (sessionsRes.ok) {
        setSessions(await sessionsRes.json());
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Fetch error:', error);
      toast.error('Failed to load patient data');
    } finally {
      setLoading(false);
    }
  };

  // Patient Summary handlers
  const handleGenerateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const response = await fetch(`${API}/patients/${patientId}/generate-summary`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setAiSummary(data.summary);
        setSummaryEdited(true);
        toast.success('Summary generated - review and edit as needed');
      }
    } catch (error) {
      toast.error('Failed to generate summary');
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleSaveSummary = async () => {
    setSavingSummary(true);
    try {
      const response = await fetch(`${API}/patients/${patientId}/summary`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ summary: aiSummary })
      });

      if (response.ok) {
        setSummaryEdited(false);
        toast.success('Summary saved');
      }
    } catch (error) {
      toast.error('Failed to save summary');
    } finally {
      setSavingSummary(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/patients/${patientId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          age: formData.age ? parseInt(formData.age) : null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update patient');
      }

      const updated = await response.json();
      setPatient(updated);
      setEditing(false);
      toast.success('Patient updated successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API}/patients/${patientId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete patient');
      }

      toast.success('Patient deleted');
      navigate('/patients');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Progress Notes handlers
  const handleAddProgressNote = async () => {
    if (!newProgressNote.trim()) return;
    
    if (!patientId) {
      toast.error('Patient ID not found');
      if (process.env.NODE_ENV !== 'production') console.error('Progress note error: patientId is undefined');
      return;
    }
    
    setAddingNote(true);
    try {
      if (process.env.NODE_ENV !== 'production') console.log('Adding progress note:', { patientId, note: newProgressNote.substring(0, 50) });
      
      const response = await fetch(`${API}/patients/${patientId}/progress-notes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ note: newProgressNote, gpt_suggested: false })
      });

      if (process.env.NODE_ENV !== 'production') console.log('Progress note response status:', response.status);

      if (response.ok) {
        const note = await response.json();
        if (process.env.NODE_ENV !== 'production') console.log('Progress note added:', note);
        setPatient(prev => ({
          ...prev,
          progress_notes: [...(prev.progress_notes || []), note]
        }));
        setNewProgressNote('');
        toast.success('Progress note added');
      } else {
        const error = await response.json();
        if (process.env.NODE_ENV !== 'production') console.error('Progress note API error:', error);
        toast.error(error.detail || 'Failed to add note');
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Progress note exception:', error);
      toast.error('Failed to add note: ' + error.message);
    } finally {
      setAddingNote(false);
    }
  };

  const handleSuggestProgressNote = async () => {
    setSuggestingNote(true);
    try {
      const response = await fetch(`${API}/patients/${patientId}/suggest-progress`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setNewProgressNote(data.suggestion);
        toast.success('AI suggestion generated - review and edit before saving');
      }
    } catch (error) {
      toast.error('Failed to generate suggestion');
    } finally {
      setSuggestingNote(false);
    }
  };

  const handleDeleteProgressNote = async (noteId) => {
    try {
      const response = await fetch(`${API}/patients/${patientId}/progress-notes/${noteId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        setPatient(prev => ({
          ...prev,
          progress_notes: (prev.progress_notes || []).filter(n => n.note_id !== noteId)
        }));
        toast.success('Note deleted');
      }
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  // Document handlers
  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/patients/${patientId}/documents`, {
        method: 'POST',
        headers: { 'Authorization': getAuthHeaders()['Authorization'] },
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const doc = await response.json();
        setPatient(prev => ({
          ...prev,
          documents: [...(prev.documents || []), doc]
        }));
        toast.success('Document uploaded');
      }
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadDocument = async (doc) => {
    window.open(`${API}/patients/${patientId}/documents/${doc.doc_id}`, '_blank');
  };

  const handleDeleteDocument = async (docId) => {
    try {
      const response = await fetch(`${API}/patients/${patientId}/documents/${docId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        setPatient(prev => ({
          ...prev,
          documents: (prev.documents || []).filter(d => d.doc_id !== docId)
        }));
        toast.success('Document deleted');
      }
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-primary">Loading patient...</div>
        </div>
      </MainLayout>
    );
  }

  if (!patient) return null;

  return (
    <MainLayout>
      <div className="space-y-4" data-testid="patient-detail-page">
        {/* Demo Badge */}
        {patient.is_demo && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-dashed border-amber-300">
            <FlaskConical className="h-5 w-5 text-amber-700" />
            <span className="text-amber-800 font-medium">Demo Patient</span>
            <span className="text-amber-700 text-sm">— Sample data for demonstration</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/patients')}
            className="rounded-xl"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 
                className="text-2xl sm:text-3xl font-semibold text-foreground"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                {patient.name}
              </h1>
              {patient.is_demo && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">Demo</Badge>
              )}
            </div>
            <p className="text-muted-foreground">
              {patient.occupation && <span>{patient.occupation} • </span>}
              {patient.age && <span>{patient.age} yrs • </span>}
              Since {format(new Date(patient.created_at), 'MMM yyyy')}
            </p>
          </div>
          <div className="flex gap-2">
            {editing ? (
              <>
                <Button variant="outline" className="rounded-xl" onClick={() => { setEditing(false); setFormData(patient); }}>
                  Cancel
                </Button>
                <Button className="rounded-xl bg-primary hover:bg-primary/90" onClick={handleSave} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" className="rounded-xl" onClick={() => setEditing(true)} data-testid="edit-patient-btn">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="rounded-xl text-destructive hover:bg-destructive/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Patient</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete {patient.name} and all associated data.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} className="bg-destructive">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </div>
        </div>

        {/* Collapsible Sections */}
        <div className="space-y-3">
          {/* Overview Section */}
          <Card className="border-border overflow-hidden">
            <Collapsible open={openSections.overview} onOpenChange={() => toggleSection('overview')}>
              <SectionHeader title="Overview" icon={FileText} isOpen={openSections.overview} />
              <CollapsibleContent>
                <CardContent className="pt-0 pb-4 px-4 space-y-4">
                  {editing ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Full Name</Label>
                        <Input name="name" value={formData.name || ''} onChange={handleInputChange} className="rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input name="email" type="email" value={formData.email || ''} onChange={handleInputChange} className="rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input name="phone" value={formData.phone || ''} onChange={handleInputChange} className="rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label>Age</Label>
                        <Input name="age" type="number" value={formData.age || ''} onChange={handleInputChange} className="rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label>Occupation</Label>
                        <Input name="occupation" value={formData.occupation || ''} onChange={handleInputChange} className="rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label>Custom Session Fee (₹)</Label>
                        <Input 
                          name="custom_fee" 
                          type="number" 
                          value={formData.custom_fee || ''} 
                          onChange={handleInputChange} 
                          className="rounded-xl" 
                          placeholder="Leave empty for default fee"
                          data-testid="custom-fee-input"
                        />
                        <p className="text-xs text-muted-foreground">Overrides default billing rate for this patient{defaultFee ? ` (default: ₹${defaultFee})` : ''}</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Referral Source</Label>
                        <Input name="referral_source" value={formData.referral_source || formData.source_of_lead || ''} onChange={handleInputChange} className="rounded-xl" placeholder="e.g., Google, Referral, Social Media" data-testid="referral-source-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Primary Concern</Label>
                        <Textarea name="primary_concern" value={formData.primary_concern || ''} onChange={handleInputChange} className="rounded-xl" />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span>{patient.email}</span>
                        </div>
                        {patient.phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <span>{patient.phone}</span>
                          </div>
                        )}
                        {patient.occupation && (
                          <div className="flex items-center gap-2 text-sm">
                            <Briefcase className="h-4 w-4 text-muted-foreground" />
                            <span>{patient.occupation}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-sm" data-testid="patient-custom-fee-display">
                          <span className="h-4 w-4 text-muted-foreground flex items-center justify-center font-bold">₹</span>
                          {patient.custom_fee > 0 ? (
                            <span>Session fee: <strong>₹{patient.custom_fee}</strong>/session (custom)</span>
                          ) : defaultFee ? (
                            <span>Session fee: <strong>₹{defaultFee}</strong>/session (default)</span>
                          ) : (
                            <span className="text-muted-foreground">Session fee: Not configured</span>
                          )}
                        </div>
                        {(patient.referral_source || patient.source_of_lead) && (
                          <div className="flex items-center gap-2 text-sm" data-testid="patient-referral-display">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span>Referral: {patient.referral_source || patient.source_of_lead}</span>
                          </div>
                        )}
                        <div className="flex gap-2 pt-2">
                          <Badge variant="outline" className={patient.consent_ai_notes ? 'badge-confirmed' : 'badge-pending'}>
                            AI Notes: {patient.consent_ai_notes ? '✓' : '✗'}
                          </Badge>
                          <Badge variant="outline" className={patient.consent_whatsapp ? 'badge-confirmed' : 'badge-pending'}>
                            WhatsApp: {patient.consent_whatsapp ? '✓' : '✗'}
                          </Badge>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Primary Concern</Label>
                        <p className="text-sm mt-1">{patient.primary_concern || 'Not specified'}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Clinical Notes Section */}
          <Card className="border-border overflow-hidden">
            <Collapsible open={openSections.clinical} onOpenChange={() => toggleSection('clinical')}>
              <SectionHeader title="Clinical Notes" icon={FileText} isOpen={openSections.clinical} />
              <CollapsibleContent>
                <CardContent className="pt-0 pb-4 px-4">
                  <Textarea
                    name="clinical_notes"
                    value={editing ? (formData.clinical_notes || '') : (patient.clinical_notes || '')}
                    onChange={handleInputChange}
                    disabled={!editing}
                    placeholder="Add free-text clinical observations, formulations, and notes..."
                    className="rounded-xl min-h-[150px]"
                  />
                  <p className="text-xs text-muted-foreground mt-2">Free-text clinical documentation. No forced structure.</p>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Previous History Section */}
          <Card className="border-border overflow-hidden">
            <Collapsible open={openSections.history} onOpenChange={() => toggleSection('history')}>
              <SectionHeader title="Previous History" icon={Clock} isOpen={openSections.history} />
              <CollapsibleContent>
                <CardContent className="pt-0 pb-4 px-4 space-y-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Previous Therapy</Label>
                    {editing ? (
                      <Textarea
                        name="previous_therapy"
                        value={formData.previous_therapy || ''}
                        onChange={handleInputChange}
                        placeholder="Past therapy experiences..."
                        className="rounded-xl mt-1"
                      />
                    ) : (
                      <p className="text-sm mt-1">{patient.previous_therapy || 'None reported'}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Medications</Label>
                    {editing ? (
                      <Textarea
                        name="medications"
                        value={formData.medications || ''}
                        onChange={handleInputChange}
                        placeholder="Current medications..."
                        className="rounded-xl mt-1"
                      />
                    ) : (
                      <p className="text-sm mt-1">{patient.medications || 'None reported'}</p>
                    )}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Progress Notes Section */}
          <Card className="border-border overflow-hidden">
            <Collapsible open={openSections.progress} onOpenChange={() => toggleSection('progress')}>
              <SectionHeader title="Progress Notes" icon={FileText} isOpen={openSections.progress} count={(patient.progress_notes || []).length} />
              <CollapsibleContent>
                <CardContent className="pt-0 pb-4 px-4 space-y-4">
                  {/* Add new note */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Textarea
                        value={newProgressNote}
                        onChange={(e) => setNewProgressNote(e.target.value)}
                        placeholder="Add a progress note..."
                        className="rounded-xl flex-1"
                        rows={2}
                        data-testid="progress-note-input"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-lg"
                        onClick={handleSuggestProgressNote}
                        disabled={suggestingNote}
                        data-testid="suggest-note-btn"
                      >
                        {suggestingNote ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                        Suggest with AI
                      </Button>
                      <Button
                        size="sm"
                        className="rounded-lg bg-primary hover:bg-primary/90"
                        onClick={handleAddProgressNote}
                        disabled={addingNote || !newProgressNote.trim()}
                        data-testid="add-note-btn"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        {addingNote ? 'Adding...' : 'Add Note'}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Existing notes */}
                  <div className="space-y-2">
                    {(patient.progress_notes || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">No progress notes yet</p>
                    ) : (
                      [...(patient.progress_notes || [])].reverse().map((note) => (
                        <div key={note.note_id} className="p-3 rounded-xl bg-muted/50 relative group">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className="text-sm">{note.note}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-xs text-muted-foreground">
                                  {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                                </span>
                                {note.gpt_suggested && (
                                  <Badge variant="outline" className="text-xs py-0">AI Suggested</Badge>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => handleDeleteProgressNote(note.note_id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Documents Section */}
          <Card className="border-border overflow-hidden">
            <Collapsible open={openSections.documents} onOpenChange={() => toggleSection('documents')}>
              <SectionHeader title="Documents" icon={Upload} isOpen={openSections.documents} count={(patient.documents || []).length} />
              <CollapsibleContent>
                <CardContent className="pt-0 pb-4 px-4 space-y-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={handleUploadDocument}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-lg"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingDoc}
                    >
                      {uploadingDoc ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                      Upload Document
                    </Button>
                    <span className="text-xs text-muted-foreground">PDF, DOC, DOCX, JPG, PNG</span>
                  </div>
                  
                  <div className="space-y-2">
                    {(patient.documents || []).length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">No documents uploaded</p>
                    ) : (
                      (patient.documents || []).map((doc) => (
                        <div key={doc.doc_id} className="flex items-center justify-between p-3 rounded-xl bg-muted/50 group">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <p className="text-sm font-medium">{doc.filename}</p>
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(doc.uploaded_at), 'MMM d, yyyy')} • {(doc.size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownloadDocument(doc)}>
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 opacity-0 group-hover:opacity-100"
                              onClick={() => handleDeleteDocument(doc.doc_id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Sessions Section */}
          <Card className="border-border overflow-hidden">
            <Collapsible open={openSections.sessions} onOpenChange={() => toggleSection('sessions')}>
              <SectionHeader title="Sessions" icon={Calendar} isOpen={openSections.sessions} count={sessions.length} />
              <CollapsibleContent>
                <CardContent className="pt-0 pb-4 px-4">
                  {sessions.length === 0 ? (
                    <div className="text-center py-6">
                      <p className="text-sm text-muted-foreground mb-2">No sessions yet</p>
                      <Button size="sm" className="rounded-lg" onClick={() => navigate('/sessions')}>
                        Schedule Session
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {sessions.slice(0, 5).map((session) => (
                        <div 
                          key={session.session_id}
                          className={`flex items-center justify-between p-3 rounded-xl bg-muted/50 hover:bg-muted cursor-pointer transition-colors ${session.is_demo ? 'border border-dashed border-amber-300' : ''}`}
                          onClick={() => navigate(`/sessions/${session.session_id}`)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-background">
                              {session.mode === 'video' ? <Video className="h-4 w-4 text-primary" /> : <MapPin className="h-4 w-4 text-primary" />}
                            </div>
                            <div>
                              <p className="text-sm font-medium">{format(new Date(session.scheduled_date), 'MMM d, yyyy')}</p>
                              <p className="text-xs text-muted-foreground">{session.scheduled_time} • {session.theme || session.mode}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Badge variant="outline" className={session.status === 'confirmed' ? 'badge-confirmed' : 'badge-tentative'}>
                              {session.status}
                            </Badge>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </div>
                      ))}
                      {sessions.length > 5 && (
                        <Button variant="link" size="sm" className="w-full" onClick={() => navigate('/sessions')}>
                          View all {sessions.length} sessions
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Patient Summary (Auto-generated) Section */}
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg" style={{ fontFamily: 'Fraunces, serif' }}>
                  Patient Summary (Auto-generated)
                </CardTitle>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg"
                  onClick={handleGenerateSummary}
                  disabled={generatingSummary || sessions.length === 0}
                  data-testid="generate-summary-btn"
                >
                  {generatingSummary ? (
                    <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Generating...</>
                  ) : (
                    <><Sparkles className="h-4 w-4 mr-1" />{aiSummary ? 'Regenerate' : 'Generate'}</>
                  )}
                </Button>
                {summaryEdited && (
                  <Button
                    size="sm"
                    className="rounded-lg bg-primary hover:bg-primary/90"
                    onClick={handleSaveSummary}
                    disabled={savingSummary}
                    data-testid="save-summary-btn"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {savingSummary ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-3">
                AI-generated summary for internal reference only. Review and edit as needed.
              </p>
              {aiSummary ? (
                <Textarea
                  value={aiSummary}
                  onChange={(e) => {
                    setAiSummary(e.target.value);
                    setSummaryEdited(true);
                  }}
                  className="min-h-[200px] rounded-xl text-sm"
                  placeholder="Patient summary will appear here..."
                  data-testid="patient-summary-textarea"
                />
              ) : (
                <div className="text-center py-8 bg-muted/30 rounded-xl">
                  <Sparkles className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground">
                    {sessions.length === 0 
                      ? 'Complete at least one session to generate a summary'
                      : 'Click "Generate" to create an AI-powered patient summary'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default PatientDetailPage;
